import type { ReactNode } from 'react';
import styles from './Alert.module.css';

export type AlertSeverity = 'info' | 'warning' | 'error';

interface AlertProps {
  severity: AlertSeverity;
  children: ReactNode;
  /** Optional inline action (e.g. "Switch", "Retry"). */
  action?: { label: string; onClick: () => void };
}

const InfoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 11v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="7.5" r="1.1" fill="currentColor" />
  </svg>
);
const WarnIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3.5l9 15.5H3L12 3.5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M12 10v4M12 16.5v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const ErrorIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ICON = { info: InfoIcon, warning: WarnIcon, error: ErrorIcon };

/**
 * The single, standard message surface for non-QC-result notices (detection hints,
 * eligibility warnings, submit failures, contact-fetch errors). The parent decides
 * which one notice to render (severity precedence); this is purely presentational.
 */
export function Alert({ severity, children, action }: AlertProps) {
  const Icon = ICON[severity];
  return (
    <div className={`${styles.alert} ${styles[severity]}`} role={severity === 'error' ? 'alert' : 'status'}>
      <span className={styles.icon}><Icon /></span>
      <span className={styles.text}>{children}</span>
      {action && (
        <button type="button" className={styles.action} onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}
